import { ICE_SERVERS } from "../config";
import type { SignalMessage } from "./signaling";

export interface PeerHandlers {
  /** Fires once the direct data channel is open. */
  onChannelOpen: (channel: RTCDataChannel) => void;
  /** Emit a signaling message to relay to the peer. */
  onSignal: (msg: SignalMessage) => void;
  onStateChange: (state: RTCPeerConnectionState) => void;
  /** ICE failed — no direct path could be established (no TURN fallback). */
  onFailed: () => void;
}

/**
 * WebRTC peer wrapper. The initiator creates the data channel and offer; the
 * responder answers. ICE candidates trickle through signaling. Once connected,
 * data flows directly peer-to-peer and the signaling server drops out.
 */
export class Peer {
  private readonly pc: RTCPeerConnection;
  private channel?: RTCDataChannel;
  private remoteReady = false;
  private pendingCandidates: RTCIceCandidateInit[] = [];

  constructor(
    private readonly initiator: boolean,
    private readonly handlers: PeerHandlers
  ) {
    this.pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });

    this.pc.onicecandidate = (e) => {
      if (e.candidate)
        handlers.onSignal({ type: "ice", candidate: e.candidate.toJSON() });
    };
    this.pc.onconnectionstatechange = () => {
      const state = this.pc.connectionState;
      handlers.onStateChange(state);
      if (state === "failed") handlers.onFailed();
    };

    if (initiator) {
      this.setupChannel(
        this.pc.createDataChannel("enclave", { ordered: true })
      );
    } else {
      this.pc.ondatachannel = (e) => this.setupChannel(e.channel);
    }
  }

  /** Initiator only: kick off the offer once both peers are present. */
  async start(): Promise<void> {
    if (!this.initiator) return;
    const offer = await this.pc.createOffer();
    await this.pc.setLocalDescription(offer);
    this.handlers.onSignal({ type: "offer", sdp: offer.sdp! });
  }

  async handleSignal(msg: SignalMessage): Promise<void> {
    switch (msg.type) {
      case "offer":
        await this.pc.setRemoteDescription({ type: "offer", sdp: msg.sdp });
        await this.flushCandidates();
        {
          const answer = await this.pc.createAnswer();
          await this.pc.setLocalDescription(answer);
          this.handlers.onSignal({ type: "answer", sdp: answer.sdp! });
        }
        break;
      case "answer":
        await this.pc.setRemoteDescription({ type: "answer", sdp: msg.sdp });
        await this.flushCandidates();
        break;
      case "ice":
        if (this.remoteReady) {
          await this.pc.addIceCandidate(msg.candidate).catch(() => void 0);
        } else {
          this.pendingCandidates.push(msg.candidate);
        }
        break;
      default:
        break;
    }
  }

  close(): void {
    try {
      this.channel?.close();
    } catch {
      /* noop */
    }
    try {
      this.pc.close();
    } catch {
      /* noop */
    }
  }

  private async flushCandidates(): Promise<void> {
    this.remoteReady = true;
    for (const c of this.pendingCandidates) {
      await this.pc.addIceCandidate(c).catch(() => void 0);
    }
    this.pendingCandidates = [];
  }

  private setupChannel(channel: RTCDataChannel): void {
    channel.binaryType = "arraybuffer";
    this.channel = channel;
    channel.onopen = () => this.handlers.onChannelOpen(channel);
  }
}
